import { isImpersonatedSession } from "@/utils/impersonation";
import * as Sentry from "@sentry/react";

/**
 * Breadcrumbs for the two surfaces that nudge a tenant to settle overdue
 * invoices — the global banner and the sidebar badge — through to the billing
 * page and the invoice PDF.
 */

type EventProperties = Record<string, unknown>;

const BREADCRUMB_CATEGORY = "billing-nudge";

export type BillingBadgeContext = {
  unpaidCount: number;
  hasOverdue: boolean;
};

export type BillingBannerContext = {
  level: "warning" | "danger";
  count: number;
  daysOverdue: number;
};

export type BillingInvoiceAction = "view" | "download";

export type BillingBalanceSource = "ledger" | "invoice_sum";

/**
 * The single exit point for this module — every tracker routes through here so
 * the infiltration gate cannot be bypassed by adding a tracker that captures on
 * its own.
 */
function emit(event: string, properties: EventProperties) {
  // An infiltrating admin is not the tenant whose funnel this measures, and
  // their clicks would otherwise read as ordinary usage of the account.
  if (isImpersonatedSession()) return;

  Sentry.addBreadcrumb({
    category: BREADCRUMB_CATEGORY,
    message: event,
    level: "info",
    data: properties,
  });
}

export function trackBillingBadgeShown(context: BillingBadgeContext): void {
  emit("billing_badge_shown", context);
}

export function trackBillingBadgeClicked(context: BillingBadgeContext): void {
  emit("billing_badge_clicked", context);
}

export function trackBillingBannerShown(context: BillingBannerContext): void {
  emit("billing_banner_shown", context);
}

export function trackBillingBannerCtaClicked(
  context: BillingBannerContext,
): void {
  emit("billing_banner_cta_clicked", context);
}

export function trackBillingBannerDismissed(
  context: BillingBannerContext,
): void {
  emit("billing_banner_dismissed", context);
}

/**
 * The eracuni ledger usually resolves after the overview query this event fires
 * on, so `balanceCents` is often still the invoice-sum fallback — which, unlike
 * `unpaidCount`, also counts eracuni-only rows. `balanceSource` says which.
 */
export function trackBillingPageViewed(properties: {
  unpaidCount: number;
  hasOverdue: boolean;
  balanceCents: number;
  balanceSource: BillingBalanceSource;
  isBillingActive: boolean;
}): void {
  emit("billing_page_viewed", properties);
}

export function trackBillingInvoiceOpened(properties: {
  action: BillingInvoiceAction;
  status: string;
}): void {
  emit("billing_invoice_opened", properties);
}
